import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import PageHeader from "@/components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Users, Shield, Truck, Radio } from "lucide-react";

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "dispatcher" });

  const fetchMembers = () => {
    axios.get(`${API}/auth/team`).then(res => { setMembers(res.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchMembers(); }, []);

  const handleAdd = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("All fields are required"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await axios.post(`${API}/auth/register`, form);
      toast.success(`${form.name} added to the team`);
      setDialogOpen(false);
      setForm({ name: "", email: "", password: "", role: "dispatcher" });
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add member");
    }
  };

  const isAdmin = user?.role === "admin";
  const roleIcons = { admin: Shield, dispatcher: Radio, driver: Truck };

  return (
    <div data-testid="team-page">
      <PageHeader title="Team Members" subtitle={`${members.length} members`}>
        {isAdmin && (
          <button data-testid="add-member-button" onClick={() => setDialogOpen(true)}
            className="h-8 px-3 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] flex items-center gap-1.5 transition-colors active:scale-95">
            <Plus className="w-3.5 h-3.5" /> Add Member
          </button>
        )}
      </PageHeader>

      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : members.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">No team members yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.map((m) => {
              const Icon = roleIcons[m.role] || Users;
              return (
                <div key={m.id} className="border border-zinc-800 bg-[#18181B] rounded-sm p-4" data-testid={`member-card-${m.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center">
                      <span className="font-heading text-sm font-bold text-zinc-400">{m.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate">{m.name}</h3>
                      <p className="text-xs text-zinc-500 truncate">{m.email}</p>
                    </div>
                    <div className="flex items-center gap-1.5 bg-zinc-800 px-2 py-1 rounded-sm">
                      <Icon className="w-3 h-3 text-orange-500" />
                      <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-medium">{m.role}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm bg-[#18181B] border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold tracking-tight">Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Full Name *</Label>
              <Input data-testid="member-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" placeholder="John Doe" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email *</Label>
              <Input data-testid="member-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" placeholder="john@sghklogistics.com.au" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Password *</Label>
              <Input data-testid="member-password-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" placeholder="Min 6 characters" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Role</Label>
              <select data-testid="member-role-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                <option value="admin">Admin</option>
                <option value="dispatcher">Dispatcher</option>
                <option value="driver">Driver</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDialogOpen(false)} className="h-9 px-4 border border-zinc-700 text-zinc-300 text-xs font-medium uppercase tracking-wider rounded-[2px] hover:bg-zinc-800">Cancel</button>
              <button data-testid="save-member" onClick={handleAdd} className="h-9 px-4 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] active:scale-95">
                Add Member
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
